import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

// components
import { PdpRespDetail } from './PdpRespDetail';
import { PdpSharedDetail, PdpSharedDetailProps } from '../PdpSharedDetails/PdpSharedDetail';
import { PdpSharedDetailCondition } from '../PdpSharedDetails/PdpSharedDetailCondition';
import { PdpSharedDetailFramingOptions } from '../PdpSharedDetails/PdpSharedDetailFramingOptions';
import { PdpSharedRingSizeDetail } from '../PdpSharedRingSize/PdpSharedRingSizeDetail';
import { PdpSharedDetailReference } from '../PdpSharedDetails/PdpSharedDetailReference';
import { PdpSharedDetailLabReport } from '../PdpSharedDetails/PdpSharedDetailLabReport';

import { detailsTitle } from '../messages/pdpDetailsMessages';

// helpers
import slugify from 'dibs-slugify/exports/slugify';

// styles
import styles from './PdpRespDetails.scss';

import {
    PdpRespDetails_item$key,
    PdpRespDetails_item,
} from './__generated__/PdpRespDetails_item.graphql';
import { PdpRespDetails_user$key } from './__generated__/PdpRespDetails_user.graphql';

const detailsToHideInPrint = ['PRODUCTION_TIME'];

type Item = NonNullable<PdpRespDetails_item>;
type PdpDetailsDisplay = NonNullable<ArrayElement<Item['pdpDetailsDisplay']>>;

type RenderDetailsProps = {
    detailKey: PdpDetailsDisplay['detailKey'];
    delimiter: PdpDetailsDisplay['delimiter'];
    detailValues: PdpSharedDetailProps['detailValues'];
};

type Props = {
    item: PdpRespDetails_item$key;
    user: PdpRespDetails_user$key;
    isClient: boolean;
    currency: string;
    selectedSkuId?: string | null;
};

export const PdpRespDetails: FC<Props> = ({
    item: itemRef,
    user: userRef,
    isClient,
    currency,
    selectedSkuId,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpRespDetails_item on Item {
                serviceId
                pdpDetailsDisplay {
                    detailKey
                    delimiter
                    detailDisplayName
                    detailValues {
                        __typename
                        ...PdpSharedDetail_detailValues
                        ...PdpSharedRingSizeDetail_detailValues
                    }
                }
                ...PdpSharedDetailFramingOptions_item
                ...PdpSharedDetailReference_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespDetails_user on User {
                ...PdpSharedDetailFramingOptions_user
                ...PdpSharedDetailReference_user
            }
        `,
        userRef
    );

    const details = item?.pdpDetailsDisplay || [];

    const RenderDetails: FC<RenderDetailsProps> = ({ detailKey, detailValues, delimiter }) => {
        const itemId = item?.serviceId;

        if (itemId) {
            if (detailKey === 'CONDITION') {
                return (
                    <PdpSharedDetailCondition
                        detailValues={detailValues}
                        currency={currency}
                        delimiter={delimiter}
                        itemId={itemId}
                    />
                );
            }
            if (detailKey === 'FRAMING') {
                return (
                    <PdpSharedDetailFramingOptions
                        detailValues={detailValues}
                        currency={currency}
                        delimiter={delimiter}
                        itemId={itemId}
                        item={item}
                        user={user}
                    />
                );
            }
            if (detailKey === 'REFERENCE_NUMBER') {
                return (
                    <PdpSharedDetailReference
                        detailValues={detailValues}
                        currency={currency}
                        delimiter={delimiter}
                        skuId={selectedSkuId}
                        item={item}
                        user={user}
                    />
                );
            }
        }
        if (detailKey === 'RING_SIZE') {
            // the Ring Size component has a different __typename=PdpDetailRingSize vs PdpDetailValue
            // @ts-ignore too long to leave comment about the type error
            return <PdpSharedRingSizeDetail isClient={isClient} detailValues={detailValues} />;
        }
        if (detailKey === 'LAB_REPORT') {
            return (
                <PdpSharedDetailLabReport
                    isClient={isClient}
                    detailValues={detailValues}
                    currency={currency}
                    delimiter={delimiter}
                    selectedSkuId={selectedSkuId}
                />
            );
        }

        return (
            <PdpSharedDetail
                detailValues={detailValues}
                currency={currency}
                delimiter={delimiter}
                selectedSkuId={selectedSkuId}
            />
        );
    };

    return (
        <div className={styles.wrapper} data-tn="pdp-details">
            <h2 className={styles.header}>{detailsTitle}</h2>
            <ul className={styles.detailsWrapper}>
                {details.filter(filterNulls).map((detail, index) => {
                    const { detailKey, detailDisplayName, delimiter, detailValues } = detail;
                    const shouldHideInPrint = !!(
                        detailKey && detailsToHideInPrint.includes(detailKey)
                    );
                    return (
                        <PdpRespDetail
                            delimiter={delimiter}
                            title={detailDisplayName}
                            dataTn={slugify(detailDisplayName)}
                            key={`${detailKey}-${index}`}
                            printHide={shouldHideInPrint}
                        >
                            <RenderDetails
                                detailKey={detailKey}
                                // @ts-ignore too long to leave comment about the type error
                                detailValues={detailValues}
                                delimiter={delimiter}
                            />
                        </PdpRespDetail>
                    );
                })}
            </ul>
        </div>
    );
};
